/**
 * The admin console's data (27 §6): organisation policies and the audit log of admin actions. Counts and policies
 * only; no plan, price or payment field exists (D-004).
 */

/** What Members see without being added to a project (FR-ADMIN-012). */
export const MEMBER_PROJECT_ACCESS = ["view_all", "invite_only"] as const;
export type MemberProjectAccess = (typeof MEMBER_PROJECT_ACCESS)[number];

/** Who creates projects (FR-ADMIN-111). Guests never do. */
export const PROJECT_CREATION = ["members", "admins"] as const;
export type ProjectCreation = (typeof PROJECT_CREATION)[number];

export interface OrganisationPolicies {
  memberProjectAccess: MemberProjectAccess;
  projectCreation: ProjectCreation;
  /** Hours a session lasts at most in this organisation; undefined keeps Orthos's own length (FR-ADMIN-047). */
  sessionMaxHours: number | undefined;
}

/** Absent fields stay; `sessionMaxHours: null` returns to Orthos's own session length. */
export interface PoliciesPatch {
  memberProjectAccess?: MemberProjectAccess;
  projectCreation?: ProjectCreation;
  sessionMaxHours?: number | null;
}

/** The kinds of admin action the log records (FR-ADMIN-060). */
export const AUDIT_CATEGORIES = [
  "people",
  "invitations",
  "settings",
  "policies",
  "access",
  "allowances",
  "sign_in",
  "projects",
  "teams",
  "security",
  "data",
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

export interface AuditParty {
  /** "person" or "staff" for actors; "member", "invitation", "organisation" or "project" for targets. */
  type: string;
  id: string | undefined;
  name: string;
}

export interface AuditEvent {
  id: string;
  occurredAt: string;
  actor: AuditParty;
  category: string;
  /** e.g. member.role_changed, invitation.sent, policy.changed. */
  action: string;
  target: AuditParty;
  result: "success" | "refused";
  ipAddress: string | undefined;
  requestId: string;
  /** Before and after, or whatever else describes the action. */
  detail: Record<string, unknown>;
}

/** Newest first; `next` asks for the following page, undefined at the end. */
export interface AuditPage {
  events: AuditEvent[];
  next: string | undefined;
}

/** Every part optional. `from` and `to` are dates (`to` inclusive); `before` is a page's `next`. */
export interface AuditQuery {
  category?: AuditCategory;
  actor?: string;
  q?: string;
  from?: string;
  to?: string;
  before?: string;
  limit?: number;
}
